import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaTimesCircle } from 'react-icons/fa';
import ContactActions from './ContactActions';
import { useRevealOnScroll } from '../hooks/useRevealOnScroll';
import Loader from './Loader';
import SuccessModal from './SuccessModal';
import { sendGetInTouchEmail } from '../services/emailService';
import { useEmailForm, validateGetInTouchForm } from '../hooks/useEmailForm';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 90%;
  max-width: 1100px;
  margin: 3rem auto;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  background-color: ${({ theme }) => theme.background};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoSection = styled.div`
  flex: 1;
  background-color: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
`;

const InfoText = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const FormSection = styled.div`
  flex: 2;
  padding: 2rem;
`;

const Heading = styled.h3`
  font-size: 1.6rem;
  color: ${({ theme }) => theme.headingColor};
  margin-bottom: 2rem;
`;

const Row = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-bottom: 2px solid #ccc;
  background: transparent;
  color: ${({ theme }) => theme.text};
  outline: none;

  &:focus {
    border-bottom: 2px solid ${({ theme }) => theme.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-bottom: 2px solid #ccc;
  background: transparent;
  color: ${({ theme }) => theme.text};
  resize: none;
  outline: none;
  margin-bottom: 1.5rem;

  &:focus {
    border-bottom: 2px solid ${({ theme }) => theme.primary};
  }
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.background};
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  box-shadow: ${({ theme }) => theme.buttonShadow};
  transition: transform 0.2s ease;
  opacity: ${({ disabled }: { disabled?: boolean }) => (disabled ? 0.6 : 1)};
  pointer-events: ${({ disabled }: { disabled?: boolean }) => (disabled ? 'none' : 'auto')};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background-color: rgba(244, 67, 54, 0.1);
  border-left: 4px solid #f44336;
  border-radius: 8px;
  color: #c62828;
  margin-bottom: 1.5rem;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  svg {
    flex-shrink: 0;
    font-size: 1.25rem;
  }
`;

const GetInTouch: React.FC = () => {
  const navigate = useNavigate();
  const { ref } = useRevealOnScroll<HTMLDivElement>();
  const { isLoading, errorMessage, handleSubmit, showSuccess, closeSuccessModal } = useEmailForm({
    onSubmit: async (payload: any) => {
      // Validate form data
      const errors = validateGetInTouchForm(payload);
      if (Object.keys(errors).length > 0) {
        throw new Error(Object.values(errors).join(', '));
      }

      // Send email
      await sendGetInTouchEmail(payload.fullName, payload.email, payload.subject, payload.message);
    },
  });

  const handleSuccessAutoClose = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    navigate('/');
  };

  return (
    <Container ref={ref}>
      <Loader isVisible={isLoading && !showSuccess} message="Sending your message..." />
      <SuccessModal isVisible={showSuccess} onClose={closeSuccessModal} onAutoClose={handleSuccessAutoClose} autoCloseDuration={4000} />
      
      <InfoSection>
        <Title>Let's get in touch</Title>
        <InfoText>Prefer quick actions? Reach me instantly below.</InfoText>
        <ContactActions />
      </InfoSection>
      <FormSection>
        <Heading>Get in touch</Heading>
        {errorMessage && (
          <ErrorMessage>
            <FaTimesCircle />
            {errorMessage}
          </ErrorMessage>
        )}
        <form onSubmit={handleSubmit}>
          <Row>
            <Input
              name="fullName"
              type="text"
              placeholder="Full Name"
              required
              disabled={isLoading}
            />
            <Input
              name="email"
              type="email"
              placeholder="Email Address"
              required
              disabled={isLoading}
            />
          </Row>
          <Input
            name="subject"
            type="text"
            placeholder="Subject"
            style={{ width: '100%', marginBottom: '1rem' }}
            required
            disabled={isLoading}
          />
          <TextArea
            name="message"
            rows={5}
            placeholder="Message"
            required
            disabled={isLoading}
          />
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </FormSection>
    </Container>
  );
};

export default GetInTouch;
